/**
 * Stripe Metadata Utility Service
 *
 * This utility provides standardized metadata handling for Stripe objects
 * following best practices and conventions.
 *
 * Key principles:
 * - Consistent naming conventions with prefixes
 * - Respect Stripe's metadata limits (50 keys, 40 char keys, 500 char values)
 * - No sensitive data (PII, payment details)
 * - Clear categorization and purpose
 */

class StripeMetadataService {
  /**
   * Generate standardized metadata for payment intents
   * @param {Object} params - Payment parameters
   * @returns {Object} - Standardized metadata object
   */
  static createPaymentIntentMetadata({
    // Core identifiers
    userId,
    clientId,
    projectId,

    // Payment details
    paymentType, // 'subscription', 'domain_extension', 'one_time', etc.
    amount,
    currency = "eur",

    // Product/Service info
    productName,
    serviceName,

    // Dates
    effectiveDate,
    renewalDate,

    // Customer info (non-sensitive)
    customerType = "person", // 'person' or 'company'

    // Domain-specific (if applicable)
    domainName,
    domainYears,

    // Subscription-specific (if applicable)
    planId,
    planName,
    priceId,

    // Additional context
    source = "admin_portal", // 'admin_portal', 'client_portal', 'api'
    environment = "production",

    // Custom metadata
    customMetadata = {},
  }) {
    const metadata = {
      // Core identifiers (required)
      user_id: userId?.toString() || "anonymous",
      client_id: clientId || `CL-${Date.now().toString().slice(-4)}`,
      project_id: projectId?.toString() || "unknown",

      // Payment categorization
      payment_type: paymentType || "unknown",
      amount: amount?.toString() || "0",
      currency: currency.toLowerCase(),

      // Product/Service identification
      product_name: this.truncateString(productName || "Unknown Product", 100),
      service_name: this.truncateString(serviceName || "Unknown Service", 100),

      // Customer type
      customer_type: customerType,

      // Source tracking
      source: source,
      environment: environment,

      // Timestamps
      created_at: new Date().toISOString(),
    };

    // Add dates if provided
    if (effectiveDate) {
      metadata.effective_date = effectiveDate;
    }
    if (renewalDate) {
      metadata.renewal_date = renewalDate;
    }

    // Add domain-specific metadata
    if (domainName) {
      metadata.domain_name = this.truncateString(domainName, 100);
      metadata.domain_years = domainYears?.toString() || "1";
    }

    // Add subscription-specific metadata
    if (planId) {
      metadata.plan_id = planId.toString();
    }
    if (planName) {
      metadata.plan_name = this.truncateString(planName, 100);
    }
    if (priceId) {
      metadata.price_id = priceId;
    }

    // Add custom metadata (with validation)
    Object.entries(customMetadata).forEach(([key, value]) => {
      if (this.isValidMetadataKey(key) && this.isValidMetadataValue(value)) {
        metadata[key] = value.toString();
      }
    });

    // Validate and clean metadata
    return this.validateAndCleanMetadata(metadata);
  }

  /**
   * Generate standardized metadata for subscriptions
   * @param {Object} params - Subscription parameters
   * @returns {Object} - Standardized metadata object
   */
  static createSubscriptionMetadata({
    userId,
    clientId,
    projectId,
    planId,
    planName,
    priceId,
    amount,
    currency = "eur",
    billingCycle = "monthly",
    autoRenew = true,
    customerType = "person",
    source = "admin_portal",
    customMetadata = {},
  }) {
    const metadata = {
      // Core identifiers
      user_id: userId?.toString() || "anonymous",
      client_id: clientId || `CL-${Date.now().toString().slice(-4)}`,
      project_id: projectId?.toString() || "unknown",

      // Subscription details
      plan_id: planId?.toString() || "unknown",
      plan_name: this.truncateString(planName || "Unknown Plan", 100),
      price_id: priceId || "unknown",
      amount: amount?.toString() || "0",
      currency: currency.toLowerCase(),
      billing_cycle: billingCycle,
      auto_renew: autoRenew.toString(),

      // Customer info
      customer_type: customerType,

      // Source tracking
      source: source,
      environment: "production",

      // Timestamps
      created_at: new Date().toISOString(),
    };

    // Add custom metadata
    Object.entries(customMetadata).forEach(([key, value]) => {
      if (this.isValidMetadataKey(key) && this.isValidMetadataValue(value)) {
        metadata[key] = value.toString();
      }
    });

    return this.validateAndCleanMetadata(metadata);
  }

  /**
   * Generate standardized metadata for customers
   * @param {Object} params - Customer parameters
   * @returns {Object} - Standardized metadata object
   */
  static createCustomerMetadata({
    userId,
    clientId,
    customerType = "person",
    source = "admin_portal",
    customMetadata = {},
  }) {
    const metadata = {
      // Core identifiers
      user_id: userId?.toString() || "anonymous",
      client_id: clientId || `CL-${Date.now().toString().slice(-4)}`,

      // Customer details
      customer_type: customerType,

      // Source tracking
      source: source,
      environment: "production",

      // Timestamps
      created_at: new Date().toISOString(),
    };

    // Add custom metadata
    Object.entries(customMetadata).forEach(([key, value]) => {
      if (this.isValidMetadataKey(key) && this.isValidMetadataValue(value)) {
        metadata[key] = value.toString();
      }
    });

    return this.validateAndCleanMetadata(metadata);
  }

  /**
   * Validate metadata key according to Stripe requirements
   * @param {string} key - Metadata key
   * @returns {boolean} - Whether key is valid
   */
  static isValidMetadataKey(key) {
    if (!key || typeof key !== "string") return false;

    // Stripe limit: 40 characters (excluding brackets)
    if (key.length > 40) return false;

    // Cannot contain brackets
    if (key.includes("[") || key.includes("]")) return false;

    // Should follow naming convention (lowercase, underscores)
    const validPattern = /^[a-z][a-z0-9_]*$/;
    return validPattern.test(key);
  }

  /**
   * Validate metadata value according to Stripe requirements
   * @param {any} value - Metadata value
   * @returns {boolean} - Whether value is valid
   */
  static isValidMetadataValue(value) {
    if (value === null || value === undefined) return false;

    const stringValue = value.toString();

    // Stripe limit: 500 characters
    if (stringValue.length > 500) return false;

    return true;
  }

  /**
   * Truncate string to specified length
   * @param {string} str - String to truncate
   * @param {number} maxLength - Maximum length
   * @returns {string} - Truncated string
   */
  static truncateString(str, maxLength) {
    if (!str || typeof str !== "string") return "";
    return str.length > maxLength ? str.substring(0, maxLength) : str;
  }

  /**
   * Validate and clean metadata object
   * @param {Object} metadata - Raw metadata object
   * @returns {Object} - Cleaned metadata object
   */
  static validateAndCleanMetadata(metadata) {
    const cleaned = {};
    let keyCount = 0;
    const maxKeys = 50; // Stripe limit

    Object.entries(metadata).forEach(([key, value]) => {
      if (keyCount >= maxKeys) {
        console.warn(
          `⚠️ Stripe metadata limit reached (${maxKeys} keys). Skipping: ${key}`
        );
        return;
      }

      if (this.isValidMetadataKey(key) && this.isValidMetadataValue(value)) {
        cleaned[key] = value.toString();
        keyCount++;
      } else {
        console.warn(`⚠️ Invalid metadata key/value pair: ${key}=${value}`);
      }
    });

    return cleaned;
  }

  /**
   * Extract metadata for reporting/filtering
   * @param {Object} stripeObject - Stripe object with metadata
   * @returns {Object} - Extracted metadata
   */
  static extractMetadata(stripeObject) {
    if (!stripeObject?.metadata) return {};

    return {
      userId: stripeObject.metadata.user_id,
      clientId: stripeObject.metadata.client_id,
      projectId: stripeObject.metadata.project_id,
      paymentType: stripeObject.metadata.payment_type,
      amount: stripeObject.metadata.amount,
      currency: stripeObject.metadata.currency,
      productName: stripeObject.metadata.product_name,
      serviceName: stripeObject.metadata.service_name,
      customerType: stripeObject.metadata.customer_type,
      source: stripeObject.metadata.source,
      environment: stripeObject.metadata.environment,
      createdAt: stripeObject.metadata.created_at,
    };
  }

  /**
   * Create metadata for domain payments
   * @param {Object} domainData - Domain payment data
   * @returns {Object} - Standardized metadata
   */
  static createDomainPaymentMetadata(domainData) {
    return this.createPaymentIntentMetadata({
      userId: domainData.userId,
      clientId: domainData.clientID,
      projectId: domainData.newPlanId || domainData.planId,
      paymentType: "domain_extension",
      amount: domainData.pricePerYear * domainData.years,
      currency: domainData.currency || "eur",
      productName: domainData.domainName,
      serviceName: "Domain Extension",
      effectiveDate: domainData.effectiveDate,
      renewalDate: domainData.renewalDate,
      customerType: domainData.customerType || "person",
      domainName: domainData.domainName,
      domainYears: domainData.years,
      source: "admin_portal",
      customMetadata: {
        domain_id: domainData.domainId,
        price_per_year: domainData.pricePerYear,
        current_expiration: domainData.currentExpirationDate,
      },
    });
  }

  /**
   * Create metadata for subscription payments
   * @param {Object} subscriptionData - Subscription payment data
   * @returns {Object} - Standardized metadata
   */
  static createSubscriptionPaymentMetadata(subscriptionData) {
    return this.createPaymentIntentMetadata({
      userId: subscriptionData.userId,
      clientId: subscriptionData.clientID,
      projectId: subscriptionData.projectId,
      paymentType: "subscription",
      amount: subscriptionData.amount,
      currency: subscriptionData.currency || "eur",
      productName: subscriptionData.product || subscriptionData.planName,
      serviceName: `${subscriptionData.planName || "Subscription"} Service`,
      customerType: subscriptionData.customerType || "person",
      planId: subscriptionData.planId,
      planName: subscriptionData.planName,
      priceId: subscriptionData.priceId,
      source: "admin_portal",
      customMetadata: {
        billing_cycle: subscriptionData.billingCycle || "monthly",
        auto_renew: subscriptionData.autoRenew !== false,
      },
    });
  }
}

export default StripeMetadataService;



